import { getDomainCfg } from "../core/state";
import { ProjectDataTab } from "../core/types";
import { backendActive, initBackend, runBackendCode } from "./backend";
import { executeTerminalFrameCode } from "./executecode";
import { collectSettings, mergeAssets } from "./executiondata";
import { codeAsStringArray, getLineEnding, removeLineBreaks } from "./loccount";
import { generateResultCode } from "./resultcode";
import { checkCodePlausibilitiy, getHelpForError } from "./errorHelp";

const isStateValid = (state: any): boolean => {
  if (!state.data || !state.data.projectContent) {
    return false;
  }
  // V2-Projekte: Tabs ohne replaceKey sind erlaubt (zusätzliche Dateien)
  // Nur prüfen ob das Projekt generell lauffähig ist
  const isV2 = state.project?.version === '2.0';
  if (isV2) {
    // V2: State ist valide wenn projectContent existiert
    return true;
  }
  // Legacy: Alle Tabs brauchen replaceKey oder das Projekt hat einen globalen replaceKey
  let tabsWithoutReplaceKeys = [];
  if (state.data.projectContent.tabs) {
    tabsWithoutReplaceKeys = state.data.projectContent.tabs.filter((tab: ProjectDataTab) => tab.replaceKey === undefined)
  }
  const hasReplaceKeys = (state.project.replaceKey !== undefined || tabsWithoutReplaceKeys.length === 0)
  return (hasReplaceKeys);
}

export const initUserCodeSession = async (project: any, initCode: string) => {
  console.log("CO-BACKEND: initUserCodeSession called", {
    backendActive: backendActive(project),
    initCodeLength: initCode?.length || 0,
    projectLanguage: project?.project?.language
  });

  if (backendActive(project)) {
    console.log("CO-BACKEND: initUserCodeSession -> calling initBackend");
    await initBackend(project, initCode);
  } else {
    console.log("CO-BACKEND: initUserCodeSession -> backend not active, skipping");
  }
}


export const executeUserCode = async (project: any, runRequested: boolean): Promise<string> => {
  console.log("CO-BACKEND: executeUserCode called", {
    runRequested,
    backendActive: backendActive(project),
    language: project?.project?.language
  });

  if (!runRequested) {
    console.log("CO-BACKEND: executeUserCode -> runRequested is false, returning empty");
    return "";
  }
  //check if backend --> backend
  let preparedCode = '';
  if (backendActive(project)) {
    console.log("CO-BACKEND: executeUserCode -> backend active, calling runBackendCode");
    preparedCode = await runBackendCode(project);
  } else if (project?.project?.type === 'multifile-service') {
    preparedCode = await getMultiFileServicePayload(project);
  } else {
    console.log("CO-BACKEND: executeUserCode -> frontend project, using template");
    generateResultCode();
    preparedCode = fillUserCodeIntoTemplate(project);
  }
  return preparedCode;
}

export const getMultiFileServicePayload = async (project: any): Promise<string> => {
  // Collect all files from project state
  const files: { fileName: string, content: string }[] = [];
  const isV2 = project?.project?.version === '2.0';

  if (isV2 && project?.project?.id) {
    const { vfs } = await import("./filesystem/index");
    const projectId = project.project.id;
    const projectRoot = vfs.getProjectRoot(projectId);
    const changes = project?.data?.v2EditorChanges || {};
    const projectFiles = project?.data?.projectContent?.tabs || [];

    for (const fileDef of projectFiles) {
      const path = fileDef.path;
      if (!path) continue;

      let code = '';
      try {
        // 1. v2EditorChanges (RAM) — auch leerer String ist bewusste User-Änderung
        if (Object.prototype.hasOwnProperty.call(changes, path) && changes[path] !== undefined) {
          code = changes[path] as string;
        } else {
          // 2. VFS, dann 3. tab.code (Server-Baseline)
          const fullPath = `${projectRoot}/${path}`;
          if (await vfs.exists(fullPath)) {
            code = await vfs.readFile(fullPath);
          } else if (fileDef.code !== undefined && fileDef.code !== null) {
            code = String(fileDef.code);
          }
        }
      } catch (e) {
        console.warn(`[getMultiFileServicePayload] VFS read: ${path}`, e);
        if (!(Object.prototype.hasOwnProperty.call(changes, path) && changes[path] !== undefined) && fileDef.code !== undefined && fileDef.code !== null) {
          code = String(fileDef.code);
        }
      }

      files.push({ fileName: path, content: code });
    }
  } else if (project?.data?.projectContent?.tabs) {
    project.data.projectContent.tabs.forEach((tab: any, index: number) => {
      let code = tab.code;
      if (project.data.editorTabs) {
        const userTab = project.data.editorTabs.find((t: any) => t.index === index);
        if (userTab && userTab.code !== undefined) code = userTab.code;
      }
      files.push({ fileName: tab.path || `file${index}.txt`, content: code });
    });
  }

  // Include projectSource for legacy projects
  if (!isV2 && project?.data?.projectContent?.projectSource) {
    let source = project.data.projectContent.projectSource;
    if (project?.data?.editorCode) source = project.data.editorCode;
    files.push({ fileName: "index.html", content: source });
  }

  // Generate Base64 ZIP
  const { createZipDataBase64 } = await import("./zipping");
  const zipBase64 = await createZipDataBase64("project.zip", files);

  // Return as JSON string so AppIFrame knows it's a payload
  const payload = {
    type: "multifile-service",
    payload: zipBase64
  };

  return JSON.stringify(payload);
}

export const getErrorCode = (project: any): string => {
  if (!project || !project.data || !project.data.errors || project.data.errors.length === 0) {
    return "";
  }

  const startLine = project.data.errorLine - 1;
  const endLine = project.data.errorLine;
  const lineno = startLine < 1 ? endLine : `${startLine}-${endLine}`;
  const errorMessage = project.data.errors[0].message;

  const code = codeAsStringArray(project.data.editorCode);
  //console.log("editorcode", project.data.editorCode)
  const errorCode = [];
  if (endLine > -1) {
    //errorCode.push(code[startLine-1]);
    errorCode.push(removeLineBreaks(code[endLine - 1]));
  }

  let errorHelp = null;
  const plausibilityWarning = checkCodePlausibilitiy(code);
  if (!plausibilityWarning) {
    errorHelp = getHelpForError(project.data.errors[0].error.message);
  } else {
    errorHelp = plausibilityWarning;
  }

  const errorObject = {
    lines: errorCode,
    errorLineStart: startLine < 1 ? endLine : startLine,
    errorLineEnd: errorHelp.lineHelpEnabled !== false ? endLine : -1,
    error: errorMessage,
    errorHelp
  };

  let text = replaceKeyWithContent(
    project.data.errorContent,
    project.project.replaceKey,
    errorCode.join(getLineEnding(project.data.editorCode))
  );
  text = replaceKeyWithContent(
    text,
    "/*{error}*/",
    JSON.stringify(errorObject).replace(/"/g, '\\"')
  );

  generateResultCode();
  return text;
}

/**
 * start Code from project. Only Template and Init-Code, NO user code.
 */
export const getStartCode = (project: any): string => {
  console.log("CO-BACKEND: getStartCode called", {
    hasExecutionCode: !!project?.data?.executionCode,
    backendActive: backendActive(project),
    startCodeOnStartup: project?.cfg?.editorFeatures?.startCodeOnStartup,
    isStateValid: isStateValid(project)
  });

  let text = "";

  const abortReplacing = !project.cfg?.editorFeatures.startCodeOnStartup && !backendActive(project);
  if (!isStateValid(project) || abortReplacing) {
    console.log("CO-BACKEND: getStartCode aborted", {
      isStateValid: isStateValid(project),
      abortReplacing
    });
    return text;
  }

  text = replaceEditorCode(project.data.projectContent.projectSource, project);
  text = replaceTabsCode(text, project);
  text = replaceInitCode(text, project);
  text = replaceAssetsCode(text, project);
  text = replaceSettingsCode(text, "init", project);
  text = applyInjectJs(text, project);

  console.log("CO-BACKEND: getStartCode -> returning code (NO initialization here)", {
    backendActive: backendActive(project),
    textLength: text.length
  });
  return text;
}

export const getStartCodeAsync = async (project: any): Promise<string> => {
  if (project?.project?.type === 'multifile-service') {
    return await getMultiFileServicePayload(project);
  }
  return getStartCode(project);
}

/**
 * start Code from project. Only Template and Init-Code, NO user code.
 */
export const getLauncherCode = (project: any): string => {
  //console.log("get launcher code for exec", project)
  let text = "";

  if (!isStateValid(project)) {
    return text;
  }

  // falls multifile, liefert string empty (muss async geholt werden)
  if (project?.project?.type === 'multifile-service') {
    return "";
  }

  const code = project.data.editorCode || project.data.projectContent.projectSource;

  text = replaceEditorCode(code, project);
  text = replaceTabsCode(text, project);
  text = replaceInitCode(text, project);
  text = replaceAssetsCode(text, project);
  text = replaceSettingsCode(text, "launcher", project);
  text = applyInjectJs(text, project);

  return text;
}

export const getLauncherCodeAsync = async (project: any): Promise<string> => {
  if (project?.project?.type === 'multifile-service') {
    return await getMultiFileServicePayload(project);
  }
  return getLauncherCode(project);
}



export const fillUserCodeIntoTemplate = (project: any): string => {
  console.log("fill code for exec", project)
  let text = "";

  if (!isStateValid(project)) {
    return text;
  }

  const code = project.data.editorCode || project.data.projectContent.projectSource;

  text = replaceEditorCode(code, project);
  text = replaceTabsCode(text, project);
  text = replaceInitCode(text, project);
  text = replaceAssetsCode(text, project);
  text = replaceSettingsCode(text, "editor", project);
  text = applyInjectJs(text, project);

  return text;
}

function replaceEditorCode(code: string, state: any) {
  if (state.project?.replaceKey) {
    return replaceKeyWithContent(
      state.data.projectContent.templateCode,
      state.project.replaceKey,
      code
    );
  }

  return state.data.projectContent.templateCode;
}

const replaceTabsCode = (text: string, state: any): string => {
  let source = text;
  const isV2 = state.project?.version === '2.0';

  if (state.data.projectContent.tabs && state.data.projectContent.tabs.length > 0) {
    state.data.projectContent.tabs.forEach((tab: any, index: number) => {
      // Get code from original projectContent.tabs
      let code = tab.code;

      if (isV2) {
        // V2: Check v2EditorChanges for user edits
        const changes = state.data.v2EditorChanges || {};
        const key = tab.path || `idx-${index}`;
        if (changes[key] !== undefined) {
          code = changes[key];
        }
      } else if (state.data.editorTabs && state.data.editorTabs.length > 0) {
        // Legacy: Find user edits by index
        const userTab = state.data.editorTabs.find((editorTab: any) => editorTab.index === index);
        if (userTab && userTab.code !== undefined) {
          code = userTab.code;
        }
      }

      source = replaceKeyWithContent(
        source,
        tab.replaceKey,
        code
      );
    });
  }

  return source;
}

const replaceInitCode = (text: string, state: any): string => {
  return replaceKeyWithContent(
    text,
    state.project.initKey,
    state.data.projectContent.initCode
  );
}

const replaceAssetsCode = (text: string, state: any): string => {
  const assetsJson = mergeAssets(state);
  return replaceKeyWithContent(
    text,
    state.project.assetsKey,
    assetsJson
  );
}

function replaceSettingsCode(text: string, context: string, state: any) {

  const settingsJson = collectSettings(context, state);
  return replaceKeyWithContent(
    text,
    "{settings}",
    settingsJson
  );
}

function replaceKeyWithContent(origin: string, key: string, content: string) {
  if (key !== undefined) {
    return origin.replace(key, content);
  }

  return origin;
}

/** Fügt injiziertes JS vor dem ersten body-Tag ein (srcdoc: Fallback-Base ist Parent-URL; #-Links sonst Host-App im iframe). */
function injectIframeScriptsBeforeBody(html: string, injectJsCode: string | undefined | null): string {
  if (!injectJsCode || !String(injectJsCode).trim()) return html;
  const body = String(injectJsCode).trim();
  const scriptTag = `<script>\n${body}\n</script>\n`;
  const idx = html.search(/<body\b/i);
  if (idx !== -1) {
    return html.slice(0, idx) + scriptTag + html.slice(idx);
  }
  return html + scriptTag;
}

function applyInjectJs(html: string, project: any): string {
  return injectIframeScriptsBeforeBody(html, project?.data?.projectContent?.injectJsCode);
}

export function initTerminalFrame(state, cfg) {
  const text = replaceKeyWithContent(
    state.data.projectContent.templateCode,
    getDomainCfg().serverSettings.replaceKey,
    JSON.stringify(cfg)
  );
  console.log("initTerminalFrame", text);
  executeTerminalFrameCode(text);
  return text;
}
